import { FC, useEffect } from 'react';
import { useTranslation } from 'src/hooks/use-translation';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import CheckIcon from 'icons/monochrome/check-bold.svg';
import CloseIcon from 'icons/monochrome/close.svg';
import WarningIcon from 'icons/monochrome/warning.svg';

import { useBillingCheckout } from 'src/context/billing-checkout-context';
import { useBoolean } from 'src/hooks/use-boolean';
import { fMoney } from 'src/utils/format-number';

// ----------------------------------------------------------------------

const CONTENT_MIN_HEIGHT = 420;

type OutcomeIconProps = {
  variant: 'success' | 'error';
};

const OutcomeIcon: FC<OutcomeIconProps> = ({ variant }) => {
  const theme = useTheme();
  const Icon = variant === 'success' ? CheckIcon : WarningIcon;

  return (
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        bgcolor: `${variant}.lighter`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon width={26} height={26} color={theme.palette[variant].main} />
    </Box>
  );
};

const PendingState: FC<{ label: string }> = ({ label }) => (
  <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 10 }}>
    <LinearProgress sx={{ width: 160, borderRadius: 1 }} />
    <Typography variant="body1" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);

// ----------------------------------------------------------------------

export const PlatformCheckoutDialog: FC = () => {
  const { t } = useTranslation('', { keyPrefix: 'platform-checkout' });
  const { isOpen, stage, session, failureReason, retryCheckout, closeCheckout } =
    useBillingCheckout();

  const { value: isFrameLoaded, onTrue: onFrameLoaded, onFalse: offFrameLoaded } = useBoolean();

  // Every new provider session gets its own loading state.
  useEffect(() => {
    offFrameLoaded();
  }, [session?.checkout_url, offFrameLoaded]);

  // Closing mid-verification would leave the payment in an unknown state.
  const isDismissable = stage !== 'preparing' && stage !== 'confirming';

  return (
    <Dialog
      open={isOpen}
      onClose={isDismissable ? closeCheckout : undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!isDismissable}
    >
      <DialogTitle sx={{ pr: 7 }}>
        {t('title')}
        {session && (
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 400, mt: 0.5 }}>
            {t('amount', { amount: fMoney(session.amount), currency: session.currency })}
          </Typography>
        )}

        {isDismissable && (
          <IconButton
            size="small"
            aria-label={t('close')}
            onClick={closeCheckout}
            sx={{ position: 'absolute', top: 16, right: 16 }}
          >
            <CloseIcon width={20} height={20} />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ minHeight: CONTENT_MIN_HEIGHT, p: stage === 'checkout' ? 0 : 3 }}
      >
        {stage === 'preparing' && <PendingState label={t('preparing')} />}

        {stage === 'confirming' && <PendingState label={t('confirming')} />}

        {stage === 'checkout' && session && (
          <Box sx={{ position: 'relative', height: CONTENT_MIN_HEIGHT }}>
            {!isFrameLoaded && (
              <Box sx={{ position: 'absolute', inset: 0 }}>
                <PendingState label={t('loading-provider')} />
              </Box>
            )}
            <Box
              component="iframe"
              title={t('provider-frame')}
              src={session.checkout_url}
              onLoad={onFrameLoaded}
              allow="payment *"
              referrerPolicy="origin"
              sx={{
                display: 'block',
                width: 1,
                height: 1,
                border: 'none',
                visibility: isFrameLoaded ? 'visible' : 'hidden',
              }}
            />
          </Box>
        )}

        {stage === 'success' && (
          <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 9 }}>
            <OutcomeIcon variant="success" />
            <Typography variant="subtitle1">{t('success-title')}</Typography>
            {session && (
              <Typography variant="body1" color="text.secondary">
                {t('amount', { amount: fMoney(session.amount), currency: session.currency })}
              </Typography>
            )}
          </Stack>
        )}

        {stage === 'failed' && (
          <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 9 }}>
            <OutcomeIcon variant="error" />
            <Typography variant="subtitle1">{t('failed-title')}</Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              {failureReason || t('failed-description')}
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ display: isDismissable ? 'flex' : 'none' }}>
        {stage === 'success' && (
          <Button color="primary" variant="contained" fullWidth onClick={closeCheckout}>
            {t('done')}
          </Button>
        )}

        {stage === 'failed' && (
          <>
            <Button variant="outlined" color="inherit" fullWidth onClick={closeCheckout}>
              {t('cancel')}
            </Button>
            <Button color="primary" variant="contained" fullWidth onClick={retryCheckout}>
              {t('retry')}
            </Button>
          </>
        )}

        {stage === 'checkout' && (
          <Typography variant="caption" color="text.disabled" sx={{ mx: 'auto' }}>
            {t('provider-note')}
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  );
};
